import * as React from "react";
import { CheckCircle2, Home, Link2Off } from "lucide-react";

import { Badge } from "@/lib/shadcn/ui/badge";
import { Button } from "@/lib/shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import { useGetHostHubMappingStatusQuery } from "@/store/hosthub-api";

const MappingStatusSkeleton: React.FC = () => (
    <div data-testid="mapping-status-loading" className="space-y-3" aria-hidden="true">
        {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
        ))}
    </div>
);

/**
 * HostHubMappingStatusPage is the manual-resolution status view (ADR-013, FR-021,
 * hosthub-reservation-ingestion): active apartments with no HostHub rental, and
 * HostHub rentals not yet referenced by any apartment — so the rental-mapping gate
 * can be driven to zero. Read-only: mapping happens in the apartment edit form.
 */
export default function HostHubMappingStatusPage() {
    const { data, isLoading, isError, refetch } = useGetHostHubMappingStatusQuery();

    const unmapped = data?.unmappedApartments ?? [];
    const unmatched = data?.unmatchedRentals ?? [];
    const allClear = !isLoading && !isError && unmapped.length === 0 && unmatched.length === 0;

    return (
        <div data-testid="hosthub-mapping-status-page" className="container mx-auto space-y-6 py-6">
            <div>
                <h1 className="text-3xl font-bold">Rental mapping status</h1>
                <p className="text-gray-500">
                    Apartments still need to be mapped to a HostHub rental (or marked not channel-managed) before sync can run.
                </p>
            </div>

            {isLoading && <MappingStatusSkeleton />}

            {isError && (
                <div data-testid="mapping-status-error" role="alert" className="space-y-3 rounded-md border border-destructive/50 bg-destructive/5 p-4">
                    <p className="text-sm text-destructive">Failed to load the rental mapping status.</p>
                    <Button data-testid="mapping-status-retry" variant="outline" onClick={() => refetch()}>
                        Retry
                    </Button>
                </div>
            )}

            {allClear && (
                <div data-testid="mapping-all-clear" className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
                    <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>All apartments are mapped and every HostHub rental is matched.</span>
                </div>
            )}

            {!isLoading && !isError && unmapped.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Home className="h-5 w-5" aria-hidden="true" />
                            Unmapped apartments
                            <Badge variant="secondary">{unmapped.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul data-testid="unmapped-apartments-list" className="divide-y">
                            {unmapped.map((apartment) => (
                                <li
                                    key={apartment.id}
                                    data-testid={`unmapped-apartment-${apartment.id}`}
                                    className="flex items-center justify-between py-2"
                                >
                                    <span>{apartment.name}</span>
                                    <Badge variant="outline">no rental</Badge>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            {!isLoading && !isError && unmatched.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Link2Off className="h-5 w-5" aria-hidden="true" />
                            Unmatched HostHub rentals
                            <Badge variant="secondary">{unmatched.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul data-testid="unmatched-rentals-list" className="divide-y">
                            {unmatched.map((rental) => (
                                <li
                                    key={rental.id}
                                    data-testid={`unmatched-rental-${rental.id}`}
                                    className="flex items-center justify-between py-2"
                                >
                                    <span>{rental.name}</span>
                                    <Badge variant="outline">{rental.id}</Badge>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
